import { useAuth0Session } from '@/hooks/useAuth0Session';
import { setAuthProviderAsPersistentCookie, setToken } from '@/lib/authActions';
import { AuthProvider } from '@/models/authModel';
import { revokeKindeSession } from '@/pages/api/scribeAPI';
import { store } from '@/store';
import { AUTH_PROVIDER_COOKIE } from '@/utils/auth/authProviderCookie';
import { logger } from '@/utils/logger/logger';
import { getCookie } from 'cookies-next/client';
import { useEffect, useRef } from 'react';

/** Poll fallback interval while the modal is open (ms). */
const POLL_INTERVAL_MS = 2000;

/**
 * Detects completion of the new-tab Auth0 login from the original tab by
 * watching the same-origin `heidi-auth-provider` cookie flip to `AUTH0`.
 *
 * There is no broadly-supported native cookie-change event (CookieStore is
 * Chromium-only), so we re-read the cookie when the user returns to this tab
 * (`visibilitychange` / `focus`) and poll as a fallback.
 *
 * On the flip, the order is deliberate and eliminates the token-swap 401s at
 * the source:
 *  1. **Adopt Auth0 first.** `refreshSession()` fetches the fresh Auth0 token,
 *     which we write straight into Redux (`setToken` +
 *     `setAuthProviderAsPersistentCookie`). Every ml-scribe request reads its
 *     bearer token from Redux at send time, so once this lands no request can
 *     go out with the Kinde token. If the Auth0 token isn't ready yet we do
 *     **not** proceed — the flag is released and the next tick retries, so
 *     Redux never holds a revoked token.
 *  2. **Then revoke the old Kinde session.** `revokeKindeSession(kindeToken)`
 *     posts to `/user/logout` carrying the *captured* Kinde token explicitly
 *     (Redux now holds the Auth0 token). `/user/logout` is per-token /
 *     Kinde-issuer scoped, so this revokes Kinde and never touches the fresh
 *     Auth0 session. Best-effort, once per migration: a failed revoke must not
 *     affect the already-migrated user.
 *
 * @param enabled Only watch while the migration modal is open.
 */
export function useKindeMigrationCompletion(enabled: boolean) {
  const { refreshSession } = useAuth0Session();
  const hasRevokedKinde = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const checkCompleted = async () => {
      const provider = getCookie(AUTH_PROVIDER_COOKIE);
      if (provider !== AuthProvider.AUTH0) return;

      const { authProvider, token: kindeToken } = store.getState().auth;
      if (!hasRevokedKinde.current && authProvider === AuthProvider.KINDE && kindeToken) {
        // Claim synchronously so concurrent focus/interval invocations can't
        // both enter this block (the awaits below yield the event loop).
        hasRevokedKinde.current = true;

        // 1. Adopt Auth0 BEFORE revoking, so no request is ever sent with the
        //    Kinde token. If the Auth0 token isn't ready yet, release the claim
        //    and let the next tick retry — Redux keeps its valid Kinde token
        //    and nothing gets revoked.
        //    `force: true` — the migration step genuinely needs a fresh
        //    Auth0 grant (a cache-hit on a pre-migration slot would
        //    write the wrong token into Redux). The keep-warm fallback
        //    call below stays non-forced.
        const auth0Token = await refreshSession({ force: true });
        if (!auth0Token) {
          hasRevokedKinde.current = false;
          return;
        }
        setToken(auth0Token);
        setAuthProviderAsPersistentCookie(AuthProvider.AUTH0);

        // 2. Revoke the old Kinde session with the captured Kinde token.
        await revokeKindeSession(kindeToken).catch((error) => {
          logger.error('[kinde-migration] Failed to revoke Kinde session', error);
        });
        return;
      }

      void refreshSession();
    };

    // Void-returning wrapper so the async handler can be used as an event
    // listener / interval callback without a floating-promise lint error.
    const handler = () => void checkCompleted();

    // Re-read when the user switches back from the login tab.
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('focus', handler);
    // Fallback poll for browsers/cases where the events don't fire.
    const intervalId = window.setInterval(handler, POLL_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('focus', handler);
      window.clearInterval(intervalId);
    };
  }, [enabled, refreshSession]);
}
